import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UIRouterModule } from '@uirouter/angular';
import { StateService } from '@uirouter/core';

import { CompanySearchResultsComponent } from 'src/app/app-common/components/company-search-results/company-search-results.component';
import { CompanyTreeComponent } from 'src/app/app-common/components/company-tree/company-tree.component';
import { CompanyTreeSearchService } from 'src/app/app-common/services/company-tree-search.service';
import { CompanyTreeService } from 'src/app/app-common/services/company-tree.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ComponentsModule } from 'src/app/components/components.module';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { CompanyAccessService } from 'src/app/settings/services/company-access.service';

@Component({
  selector: 'select-company-tree',
  templateUrl: './select-company-tree.component.html',
  styleUrl: './select-company-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ComponentsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    UIRouterModule,
    CompanyTreeComponent,
    CompanySearchResultsComponent,
  ],
  providers: [CompanyTreeService, CompanyTreeSearchService],
})
export class SelectCompanyTreeComponent {
  @Output() closeMenu = new EventEmitter<void>();

  private readonly companyState = inject(CompanyStateService);
  private readonly featuresService = inject(FeaturesService);
  private readonly stateService = inject(StateService);
  protected readonly companyAccessService = inject(CompanyAccessService);
  protected readonly treeService = inject(CompanyTreeService);
  protected readonly searchService = inject(CompanyTreeSearchService);

  protected readonly mode = computed(() =>
    this.searchService.query() ? 'search' : 'tree',
  );

  protected readonly showSpinner = computed(
    () =>
      this.treeService.initialStatus() === 'loading' ||
      (this.mode() === 'search' &&
        this.searchService.loading() &&
        this.searchService.results().length === 0),
  );

  protected readonly filterConfig = {
    placeholder: 'Search Sub-Company',
    borderless: true,
  };

  /**
   * Naming the destination is what makes the icon-only control usable: "Home"
   * or "Parent company" would not say where activating it lands the user.
   */
  protected readonly parentCompanyLabel = computed(() => {
    const name = this.companyState.userCompany()?.name;
    return name ? `Switch to ${name}` : 'Switch to my company';
  });

  /**
   * The text equivalent of the end-of-list spinners, which are hidden from
   * assistive technology: a progressbar is not a legal child of a tree or a
   * list, and a spinner tells a screen reader nothing.
   */
  protected readonly pagingMessage = computed(() => {
    const paging =
      this.mode() === 'search'
        ? this.searchService.loading() &&
          this.searchService.results().length > 0
        : this.treeService.loadingMore();

    return paging ? 'Loading more companies' : '';
  });

  constructor() {
    void this.treeService.initialize();
  }

  protected onShowAllCompaniesChange(checked: boolean): void {
    void this.treeService.setShowAllCompanies(checked);
  }

  protected selectParentCompany(): void {
    const userCompanyId = this.companyState.getUserCompanyId();
    if (!userCompanyId) {
      return;
    }

    void this.treeService.select(userCompanyId, 'parent-company');
    this.closeMenu.emit();
  }

  protected licenseManagement(): void {
    if (this.companyAccessService.accessCompanyLicenses) {
      this.stateService.go('apps.settings.company-licenses');
      this.closeMenu.emit();
    } else {
      this.featuresService.showUpgradePlanModal(
        FeaturesService.FEATURE_LICENSE_MANAGEMENT,
      );
    }
  }
}
